// #3753 (Gilbert, 2026-10-20: "Fresh guest on mismatch").
//
// A guest's token lives in localStorage and used to be reused on every
// landing, whatever organization it was minted into. A guest's org is fixed at
// mint time (backend `AuthService.resolveGuestOrganization`), and run creation
// scopes pathway inference by that org (`services/runs.js` INFER/EXPLICIT
// pathway SQL, `ps.organization_id = $4`). So a Yoma guest who opened a public
// TRIAL scenario (the /applying-for-jobs tools, the "Applying for jobs"
// pathway) started it as a Yoma user: the run got pathway_id NULL, so no
// memory carry and no "Continue to next stage", silently.
//
// The rule: a guest is reused only when it is in the org a FRESH guest minted
// through this same entry point would land in (`GET /auth/guest/organization`,
// which runs the mint's own resolver). On a mismatch, mint a fresh guest.
//
// This module is only the pure decision; `AuthContext.loginAsGuest` is the one
// place that acts on it. It never sees a real (non-guest) session: the caller
// refuses those before getting here (#3406).

/**
 * @param {object} params
 * @param {unknown} params.guestOrgId  - the stored guest's organization_id (from /auth/me or the mint response)
 * @param {unknown} params.targetOrgId - the org a fresh mint for this entry would land in, or null when unknown
 * @param {unknown} [params.guestHasCapturedEmail] - the stored guest's
 *   `has_pending_email` from /auth/me (true once it gave an email, e.g. "Keep
 *   this plan"). Only the literal `true` counts.
 * @returns {'reuse'|'mint'} 'mint' ONLY when both ids are known and differ
 *   AND the guest has not captured an email. Anything unknown (prediction
 *   failed, a user object without an org) keeps the current guest: an unknown
 *   must never cost a learner their identity.
 *
 *   #3753 review round 2 (P2): a guest that captured an email is always kept.
 *   There is one token slot, so a mint would overwrite it, and a later Google
 *   sign-in nominates the guest in that slot (the bearer token) before the
 *   one found by `pending_email` (AuthService.resolveGuestForConversion): the
 *   new, empty guest would be converted and the history the learner asked to
 *   keep would never reach their account. Keeping it costs only this run's
 *   pathway inference, which is the pre-#3753 behaviour.
 */
export function decideGuestOrgReuse({ guestOrgId, targetOrgId, guestHasCapturedEmail = false }) {
  const guest = Number(guestOrgId);
  const target = Number(targetOrgId);
  const known = (n) => Number.isInteger(n) && n > 0;
  if (guestHasCapturedEmail === true) return 'reuse';
  if (guestOrgId == null || targetOrgId == null) return 'reuse';
  if (!known(guest) || !known(target)) return 'reuse';
  return guest === target ? 'reuse' : 'mint';
}

/**
 * Cache key for a prediction: the exact two inputs the mint resolves on.
 * @param {{audience?: string|null, scenarioId?: number|string|null}} entry
 */
export function guestOrgEntryCacheKey({ audience = null, scenarioId = null } = {}) {
  const sId = Number(scenarioId);
  return `${typeof audience === 'string' ? audience : ''}|${Number.isInteger(sId) && sId > 0 ? sId : ''}`;
}

/**
 * Should a guest-entry caller (scenario landing, discovery start, home card)
 * call `loginAsGuest()` before starting a run?
 *
 * Before #3753 the answer was `!isAuthenticated`, so a tab already holding a
 * verified guest never reached the org check and started a trial scenario as,
 * say, a Yoma guest. A guest now always goes through loginAsGuest, which is a
 * no-op (no write, no mint) when its org already matches. A REAL signed-in
 * user never does: loginAsGuest must never be asked to act over a real session.
 *
 * @param {{isAuthenticated: boolean, user: object|null|undefined}} params
 */
export function shouldEnsureGuestSession({ isAuthenticated, user }) {
  if (!isAuthenticated) return true;
  return user?.is_guest === true;
}
